/**
 * StockAnalysisPage - Stock analysis dashboard with session history.
 *
 * Layout:
 * - Left: Sidebar with navigation + financial session history
 * - Main: Research hero (idle), ProcessBar + LogicTree (researching),
 *         DecisionSheet + FollowUpChat (done)
 *
 * States:
 * - Idle: StockResearchHero with search input + market intelligence
 * - Researching: FinancialLogicTree inside ProcessBar (streaming)
 * - Conversation: Collapsed ProcessBar + FinancialDecisionSheet + FollowUpChat
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RotateCcw, ArrowLeft } from 'lucide-react';
import { PageContextProvider, usePageContextUpdater } from '@/contexts/PageContext';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePortfolio } from '@/hooks/usePortfolio';
import { useFinancialStream } from '@/hooks/useFinancialStream';
import { useResearchSessions } from '@/hooks/useResearchSessions';
import { useCredits } from '@/hooks/useCredits';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { Sidebar } from '@/components/layout/Sidebar';
import { ResearchSidebarContent } from '@/components/verdict/ResearchSidebarContent';
import { FinancialLogicTree, type FinancialResearchDepth, type FinancialStreamingState } from '@/components/verdict/FinancialLogicTree';
import { FinancialDecisionSheet } from '@/components/verdict/FinancialDecisionSheet';
import { ProcessBar } from '@/components/verdict/ProcessBar';
import { FollowUpChat } from '@/components/verdict/FollowUpChat';
import { getFinancialProcessMetadata } from '@/utils/processBarMetadata';
import { restoreFinancialState, hasFullFinancialState } from '@/utils/restoreState';
import { CreditConsumedToast, InsufficientCreditsDialog } from '@/components/credits';
import { StockResearchHero } from '@/components/stock-analysis/StockResearchHero';
import { TickerLogo } from '@/components/ui/TickerLogo';
import type { MarketNewsItem } from '@/components/stock-analysis/MarketNewsSection';
import type { MarketMover } from '@/components/stock-analysis/TrendingTickersCard';
import type { ResearchIntensity, ResearchMode } from '@/types';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------
interface MarketPulseData {
  market_news: MarketNewsItem[];
  top_gainers: MarketMover[];
  top_losers: MarketMover[];
  most_active: MarketMover[];
}

type UIState = 'idle' | 'researching' | 'conversation';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// --------------------------------------------------------------------------
// Page Component
// --------------------------------------------------------------------------
export function StockAnalysisPage() {
  // ---- Auth ----
  const { user } = useAuth();
  const isAuthenticated = !!user;

  // ---- URL params ----
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialTickerParam = searchParams.get('ticker') || '';

  // Clear URL params after consuming them
  useEffect(() => {
    if (initialQuery || initialTickerParam) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Portfolio data (for context) ----
  const { positions } = usePortfolio();

  // ---- Market pulse ----
  const [marketPulse, setMarketPulse] = useState<MarketPulseData | null>(null);
  const [isLoadingPulse, setIsLoadingPulse] = useState(false);

  useEffect(() => {
    setIsLoadingPulse(true);
    const headers = getAuthHeaders();
    fetch(`${API_BASE_URL}/api/v1/financial/market-pulse`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setMarketPulse(data); })
      .catch(() => {})
      .finally(() => setIsLoadingPulse(false));
  }, []);

  // ---- UI state ----
  const [uiState, setUIState] = useState<UIState>('idle');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const userManuallyToggled = useRef(false);
  const isMobile = useIsMobile();

  // ---- Session management (financial sessions) ----
  const {
    sessions,
    currentSession,
    isLoading: sessionsLoading,
    isLoadingSession,
    pendingSessionId,
    createSession,
    selectSession,
    addMessage,
    archiveSession,
    clearCurrentSession,
  } = useResearchSessions('financial');

  // ---- Credit system ----
  const { balance, isUnlimited, refreshBalance, hasSufficientCredits, packs, fetchPacks, purchaseCredits } = useCredits();
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const preAnalysisBalanceRef = useRef<number | null>(null);
  const [creditToast, setCreditToast] = useState<{ creditsUsed: number; newBalance: number } | null>(null);

  // ---- Active analysis query ----
  const [activeQuery, setActiveQuery] = useState('');
  const [activeTicker, setActiveTicker] = useState<string | null>(null);

  // ---- Restored state (from session selection) ----
  const [restoredState, setRestoredState] = useState<FinancialStreamingState | null>(null);

  // ---- Follow-up chat state ----
  const [chatExpanded, setChatExpanded] = useState(false);
  const [sessionMessages, setSessionMessages] = useState<Array<{
    id: string;
    role: 'user' | 'council' | 'system';
    content: string;
    type: 'query' | 'verdict' | 'followup' | 'chat';
    confidence?: 'high' | 'medium' | 'low';
    suggestResearch?: boolean;
    createdAt: string;
  }>>([]);

  // ---- Financial stream ----
  const {
    state: financialState,
    startAnalysis,
    reset: resetFinancialAnalysis,
  } = useFinancialStream({
    onComplete: () => {
      setUIState('conversation');
      // Credit toast
      const prevBal = preAnalysisBalanceRef.current;
      refreshBalance().then((result) => {
        if (result && prevBal !== null && !isUnlimited) {
          const used = prevBal - result.credit_balance;
          if (used > 0) setCreditToast({ creditsUsed: used, newBalance: result.credit_balance });
        }
        preAnalysisBalanceRef.current = null;
      });
    },
    onError: (err) => {
      console.error('Stock analysis error:', err);
      setUIState('idle');
    },
  });

  const isDone = financialState.phase === 'concluded';

  // ---- Page context for debate panel ----
  const { setPageContext, clearPageContext } = usePageContextUpdater();
  useEffect(() => {
    setPageContext({
      pageType: 'stock-analysis',
      tickers: activeTicker ? [activeTicker] : undefined,
      selectedTicker: activeTicker || undefined,
    });
    return () => clearPageContext();
  }, [activeTicker, setPageContext, clearPageContext]);

  // Load packs lazily
  useEffect(() => { if (isAuthenticated) fetchPacks(); }, [isAuthenticated, fetchPacks]);

  // Handle insufficient credits from SSE
  useEffect(() => {
    if (financialState.insufficientCredits) {
      setShowInsufficientCredits(true);
    }
  }, [financialState.insufficientCredits]);

  // Auto-collapse sidebar during research (desktop)
  useEffect(() => {
    if (isMobile) return;
    if (userManuallyToggled.current) return;
    if (uiState === 'researching') {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [uiState, isMobile]);

  useEffect(() => {
    if (uiState === 'idle') {
      userManuallyToggled.current = false;
    }
  }, [uiState]);

  // ---- Save to session on completion ----
  useEffect(() => {
    if (!isDone || !financialState.killSheet || !isAuthenticated) return;

    const saveToSession = async () => {
      const ticker = financialState.ticker || activeTicker || 'Unknown';
      const sessionTitle = `${ticker} Analysis`;

      // Create session if none exists
      let sessionId = currentSession?.id;
      if (!sessionId) {
        const newSession = await createSession(sessionTitle);
        sessionId = newSession?.id;
      }

      if (sessionId) {
        // Save the query as user message
        await addMessage(sessionId, activeQuery || `Analyze ${ticker}`, 'query', 'user');

        // Save the verdict with full state for reconstruction
        const verdictPayload = JSON.stringify({
          verdict: `Financial analysis completed for ${ticker}`,
          kill_sheet: financialState.killSheet,
          anchored_snapshot: financialState.hardDataSnapshot,
          blueprint: { topic: activeQuery || `Analyze ${ticker}`, mode: 'financial' },
          full_state: {
            version: 1,
            ticker: financialState.ticker || ticker,
            depth: financialState.depth,
            bullAgents: financialState.bullAgents,
            bearAgents: financialState.bearAgents,
            debateMessages: financialState.debateMessages,
            debateRound: financialState.debateRound,
            agreementScore: financialState.agreementScore,
            mediatorCompilation: financialState.mediatorCompilation,
            killSheet: financialState.killSheet,
            hardDataSnapshot: financialState.hardDataSnapshot,
            convergenceTrajectory: financialState.convergenceTrajectory,
            initialDivergence: financialState.initialDivergence,
            echoChamberedDetected: financialState.echoChamberedDetected,
            totalCreditsUsed: financialState.totalCreditsUsed,
            isComparison: financialState.isComparison,
            comparisonTickerA: financialState.comparisonTickerA,
            comparisonTickerB: financialState.comparisonTickerB,
            comparisonKillSheet: financialState.comparisonKillSheet,
            comparisonSnapshotA: financialState.comparisonSnapshotA,
            comparisonSnapshotB: financialState.comparisonSnapshotB,
          },
        });
        await addMessage(sessionId, verdictPayload, 'verdict', 'assistant');

        // Build follow-up messages from the session
        setSessionMessages([
          {
            id: `query-${Date.now()}`,
            role: 'user',
            content: activeQuery || `Analyze ${ticker}`,
            type: 'query',
            createdAt: new Date().toISOString(),
          },
        ]);
      }
    };

    saveToSession().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone]);

  // ---- Also auto-save to deep-dive history (legacy, for portfolio page) ----
  useEffect(() => {
    if (isDone && financialState.killSheet && activeTicker) {
      const headers = getAuthHeaders();
      fetch(`${API_BASE_URL}/api/v1/portfolio/deep-dive/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ticker: activeTicker.toUpperCase(),
          kill_sheet: financialState.killSheet,
          snapshot: financialState.hardDataSnapshot,
          winning_side: (financialState.killSheet as any)?.winner?.side || (financialState.killSheet as any)?.winningSide,
          confidence: (financialState.killSheet as any)?.winner?.confidence || (financialState.killSheet as any)?.confidence,
          full_state: {
            version: 1,
            ticker: financialState.ticker || activeTicker,
            depth: financialState.depth,
            bullAgents: financialState.bullAgents,
            bearAgents: financialState.bearAgents,
            debateMessages: financialState.debateMessages,
            debateRound: financialState.debateRound,
            agreementScore: financialState.agreementScore,
            mediatorCompilation: financialState.mediatorCompilation,
            killSheet: financialState.killSheet,
            hardDataSnapshot: financialState.hardDataSnapshot,
            convergenceTrajectory: financialState.convergenceTrajectory,
            initialDivergence: financialState.initialDivergence,
            echoChamberedDetected: financialState.echoChamberedDetected,
            totalCreditsUsed: financialState.totalCreditsUsed,
            isComparison: financialState.isComparison,
            comparisonTickerA: financialState.comparisonTickerA,
            comparisonTickerB: financialState.comparisonTickerB,
            comparisonKillSheet: financialState.comparisonKillSheet,
            comparisonSnapshotA: financialState.comparisonSnapshotA,
            comparisonSnapshotB: financialState.comparisonSnapshotB,
          },
        }),
      }).catch(() => {});
    }
  }, [isDone, financialState.killSheet, financialState.hardDataSnapshot, activeTicker]);

  // ---- Handlers ----

  const handleStartAnalysis = useCallback((query: string, ticker: string | null, depth: FinancialResearchDepth) => {
    // Pre-flight credit check
    if (balance !== null && !isUnlimited) {
      const estimatedCredits = depth === 'deep' ? 30 : 15;
      if (!hasSufficientCredits(estimatedCredits)) {
        setShowInsufficientCredits(true);
        return;
      }
    }

    // Snapshot balance for consumption toast
    if (balance !== null && !isUnlimited) {
      preAnalysisBalanceRef.current = balance;
    }

    // Clear any restored state
    setRestoredState(null);
    setSessionMessages([]);
    clearCurrentSession();

    setActiveQuery(query);
    setActiveTicker(ticker || (query.match(/\b([A-Z]{1,5})\b/)?.[1] || null));
    setUIState('researching');

    const isHeld = ticker ? positions.some(p => p.symbol === ticker.toUpperCase()) : false;
    startAnalysis(ticker, depth, query, { portfolioContext: isHeld });
  }, [balance, isUnlimited, hasSufficientCredits, positions, startAnalysis, clearCurrentSession]);

  const handleNewResearch = useCallback(() => {
    resetFinancialAnalysis();
    setActiveQuery('');
    setActiveTicker(null);
    setRestoredState(null);
    setSessionMessages([]);
    clearCurrentSession();
    setUIState('idle');
    setChatExpanded(false);
  }, [resetFinancialAnalysis, clearCurrentSession]);

  // Session selection handler - restore state from session messages
  const handleSelectSession = useCallback(async (sessionId: string) => {
    await selectSession(sessionId);
  }, [selectSession]);

  // When currentSession changes, restore its state
  useEffect(() => {
    if (!currentSession?.messages?.length) return;

    // Find the verdict message (contains the full state)
    const verdictMsg = [...currentSession.messages]
      .reverse()
      .find(m => m.message_type === 'verdict');

    if (verdictMsg) {
      try {
        const parsed = JSON.parse(verdictMsg.content);
        if (parsed.full_state && hasFullFinancialState(parsed.full_state)) {
          const restored = restoreFinancialState(parsed.full_state);
          setRestoredState(restored);
          setActiveQuery(parsed.blueprint?.topic || 'Analysis');
          setActiveTicker(restored.ticker || null);
          setUIState('conversation');

          // Build session messages for FollowUpChat
          const msgs = currentSession.messages
            .filter(m => m.message_type === 'followup' || m.message_type === 'query')
            .map(m => ({
              id: m.id,
              role: (m.role === 'assistant' ? 'council' : m.role) as 'user' | 'council' | 'system',
              content: m.content,
              type: m.message_type as 'query' | 'verdict' | 'followup' | 'chat',
              createdAt: m.created_at,
            }));
          setSessionMessages(msgs);
          return;
        }
      } catch {
        // Not JSON or missing full_state — treat as basic session
      }
    }

    // Fallback: show session as idle (can't restore without full_state)
    setUIState('idle');
  }, [currentSession]);

  // Handle follow-up research deeper
  const handleResearchDeeper = useCallback((query: string, _intensity: ResearchIntensity, _mode: ResearchMode) => {
    handleStartAnalysis(query, null, 'deep');
  }, [handleStartAnalysis]);

  // Get the effective state (live or restored)
  const effectiveState = restoredState || financialState;
  const effectiveIsDone = restoredState ? true : isDone;
  const effectiveTicker = effectiveState.ticker || activeTicker;

  // ---- Render ----
  return (
    <PageContextProvider>
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => {
          userManuallyToggled.current = true;
          setSidebarCollapsed(prev => !prev);
        }}
      >
        {isAuthenticated && (
          <ResearchSidebarContent
            sessions={sessions}
            currentSessionId={currentSession?.id}
            onSelectSession={handleSelectSession}
            onNewSession={handleNewResearch}
            onArchiveSession={archiveSession}
            isLoading={sessionsLoading}
            pendingSessionId={pendingSessionId}
            isLoadingSession={isLoadingSession}
          />
        )}
      </Sidebar>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header spacing */}
        <div className="md:hidden h-16 shrink-0" />

        <div className="flex-1 overflow-y-auto">
          <div className={cn(
            'mx-auto px-4 py-6',
            uiState === 'idle' ? 'max-w-4xl' : 'max-w-5xl',
          )}>
            {/* Loading session */}
            {isLoadingSession && (
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="mt-3 text-text-muted">Loading session...</p>
              </div>
            )}

            {/* Idle State: StockResearchHero */}
            {uiState === 'idle' && !isLoadingSession && (
              <StockResearchHero
                onTickerSelect={(ticker) => setActiveTicker(ticker)}
                onDeepAnalysis={(ticker) => handleStartAnalysis(`Analyze ${ticker} stock`, ticker, 'fast')}
                onAnalysisStart={handleStartAnalysis}
                marketPulse={marketPulse}
                isLoadingPulse={isLoadingPulse}
                summary={null}
                positionCount={positions.length}
                positionsWithScan={[]}
                portfolioPositions={positions}
                initialQuery={initialQuery}
                initialTicker={initialTickerParam}
              />
            )}

            {/* Researching State: ProcessBar + FinancialLogicTree */}
            {uiState === 'researching' && !isLoadingSession && (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {effectiveTicker && (
                      <>
                        <TickerLogo symbol={effectiveTicker} size="md" />
                        <div>
                          <h2 className="text-lg font-bold text-text-primary font-mono">
                            {effectiveTicker}
                          </h2>
                          <p className="text-xs text-text-muted">Analyzing...</p>
                        </div>
                      </>
                    )}
                    {!effectiveTicker && (
                      <div>
                        <h2 className="text-lg font-bold text-text-primary">Running Analysis</h2>
                        <p className="text-xs text-text-muted truncate max-w-[300px]">{activeQuery}</p>
                      </div>
                    )}
                  </div>
                </div>

                {financialState.phase !== 'idle' && (
                  <ProcessBar
                    metadata={getFinancialProcessMetadata(financialState)}
                    defaultExpanded={true}
                    animateCollapse={false}
                  >
                    <FinancialLogicTree
                      state={financialState}
                      query={activeQuery}
                      hideActions
                    />
                  </ProcessBar>
                )}
              </div>
            )}

            {/* Conversation State: Collapsed ProcessBar + DecisionSheet + FollowUpChat */}
            {uiState === 'conversation' && !isLoadingSession && (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleNewResearch}
                      className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-light transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    {effectiveTicker && (
                      <>
                        <TickerLogo symbol={effectiveTicker} size="md" />
                        <div>
                          <h2 className="text-lg font-bold text-text-primary font-mono">
                            {effectiveTicker}
                          </h2>
                          <p className="text-xs text-text-muted">Analysis Complete</p>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={handleNewResearch}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    New Analysis
                  </button>
                </div>

                {/* Collapsed ProcessBar with LogicTree */}
                <ProcessBar
                  metadata={getFinancialProcessMetadata(effectiveState)}
                  defaultExpanded={false}
                  animateCollapse={!restoredState}
                >
                  <FinancialLogicTree
                    state={effectiveState}
                    query={activeQuery}
                    hideActions
                  />
                </ProcessBar>

                {/* Decision Sheet */}
                {effectiveState.killSheet && (
                  <FinancialDecisionSheet
                    killSheet={effectiveState.killSheet}
                    anchoredSnapshot={effectiveState.hardDataSnapshot}
                    convergenceTrajectory={effectiveState.convergenceTrajectory}
                    initialDivergence={effectiveState.initialDivergence}
                    echoChamberedDetected={effectiveState.echoChamberedDetected}
                    query={activeQuery}
                  />
                )}

                {/* Follow-up Chat */}
                {isAuthenticated && currentSession?.id && effectiveIsDone && (
                  <FollowUpChat
                    sessionId={currentSession.id}
                    initialMessages={sessionMessages}
                    onResearchDeeper={handleResearchDeeper}
                    isResearching={false}
                    creditBalance={balance}
                    isUnlimited={isUnlimited}
                    isExpanded={chatExpanded}
                    onToggleExpand={() => setChatExpanded(prev => !prev)}
                    className="mt-6"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Credit Consumed Toast */}
      {creditToast && (
        <CreditConsumedToast
          creditsUsed={creditToast.creditsUsed}
          newBalance={creditToast.newBalance}
          onDismiss={() => setCreditToast(null)}
        />
      )}

      {/* Insufficient Credits Dialog */}
      <InsufficientCreditsDialog
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        currentBalance={financialState.insufficientCredits?.available ?? balance ?? 0}
        requiredCredits={financialState.insufficientCredits?.required ?? 30}
        queryType="financial_deep_dive"
        packs={packs}
        onPurchase={purchaseCredits}
      />
    </div>
    </PageContextProvider>
  );
}
